import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { useAssistantServices } from '../AssistantServices'

export type Feedback = { rating: 'up' | 'down'; comment?: string }

type FeedbackApi = {
  ratingOf(threadId: string, messageId: string): Feedback | undefined
  /**
   * Resolves `true` when this was still the newest choice for the message once it was
   * saved, and `false` when a later choice replaced it (its result then no longer matters).
   * Rejects only when the newest choice could not be saved.
   */
  save(threadId: string, messageId: string, feedback: Feedback): Promise<boolean>
}

const FeedbackContext = createContext<FeedbackApi | null>(null)

const keyOf = (threadId: string, messageId: string) => `${threadId}\u0000${messageId}`

/**
 * The ratings given during this visit. The agent has no route that returns a stored
 * rating, so a reload starts without them. A rating shows at once and is put back if
 * the save fails. Saves of one message run one after the other.
 */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { threads } = useAssistantServices()
  const [ratings, setRatings] = useState<Record<string, Feedback>>({})
  const current = useRef<Record<string, Feedback>>({})
  // What the server last took. A failed save goes back to this, never to a value that was only on screen.
  const confirmed = useRef<Record<string, Feedback>>({})
  const queues = useRef(new Map<string, Promise<unknown>>())
  const latest = useRef(new Map<string, number>())

  const write = useCallback((key: string, value: Feedback | undefined) => {
    const next = { ...current.current }

    if (value) next[key] = value
    else delete next[key]

    current.current = next
    setRatings(next)
  }, [])

  const save = useCallback(
    async (threadId: string, messageId: string, feedback: Feedback) => {
      const key = keyOf(threadId, messageId)
      const sequence = (latest.current.get(key) ?? 0) + 1

      latest.current.set(key, sequence)
      write(key, feedback)

      const run = (queues.current.get(key) ?? Promise.resolve())
        .catch(() => undefined)
        .then(() => threads.saveFeedback(threadId, messageId, feedback))

      queues.current.set(key, run.catch(() => undefined))

      try {
        await run
        confirmed.current[key] = feedback

        return latest.current.get(key) === sequence
      } catch (error) {
        // A choice that a later one replaced has nothing left to undo or to report.
        if (latest.current.get(key) !== sequence) return false

        write(key, confirmed.current[key])

        throw error
      }
    },
    [threads, write]
  )

  const api = useMemo<FeedbackApi>(
    () => ({ ratingOf: (threadId, messageId) => ratings[keyOf(threadId, messageId)], save }),
    [ratings, save]
  )

  return <FeedbackContext.Provider value={api}>{children}</FeedbackContext.Provider>
}

export function useFeedback(): FeedbackApi {
  const api = useContext(FeedbackContext)

  if (!api) throw new Error('useFeedback needs a FeedbackProvider')

  return api
}
